import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Landmark, Car, Home, Receipt, Megaphone, HandCoins, Banknote,
  ShieldCheck, Users, GraduationCap, BadgeCheck, Wrench, FlaskConical, Building2,
  Cpu, Plane, Zap, Package, Crown, CheckSquare, FileText, Clock, AlertTriangle, Sparkles,
  Calculator, Plus, Bell, PiggyBank, Download,
} from "lucide-react";
import api from "@/lib/api";
import Modal from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { usePremium } from "@/context/PremiumContext";

const money2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KEY_NUMBERS = [
  { label: "Business mileage rate", value: "72.5¢ / 76¢", sub: "per mile · Jan–Jun / Jul–Dec 2026", Icon: Car },
  { label: "Section 179 limit", value: "$2,560,000", sub: "phase-out begins $4.09M · heavy SUV cap $32,000", Icon: Landmark },
  { label: "Home office (simplified)", value: "$5 / sq ft", sub: "up to 300 sq ft → $1,500 max", Icon: Home },
  { label: "1099-NEC threshold", value: "$600", sub: "per contractor / yr (may rise to $2,000 — verify)", Icon: Receipt },
  { label: "Business meals", value: "50%", sub: "deductible · entertainment 0%", Icon: HandCoins },
  { label: "Bonus depreciation", value: "100%", sub: "permanent for assets placed in service after 1/19/2025", Icon: Sparkles },
];

const CATEGORIES = [
  {
    name: "Advertising and Marketing", Icon: Megaphone,
    desc: "Anything you spend to get your business in front of customers.",
    examples: "Online & social ads, business cards, website & domain, signage, branded merch, flyers.",
    rule: "Fully deductible when ordinary and necessary for your trade.",
  },
  {
    name: "Bad Debt", Icon: Banknote,
    desc: "Money owed to you that you genuinely can't collect.",
    examples: "Unpaid invoices you already reported as income, uncollectible business loans.",
    rule: "Cash-basis service businesses usually can't deduct unpaid invoices — the income was never counted.",
  },
  {
    name: "Banking Interest and Fees", Icon: HandCoins,
    desc: "Interest on business debt and the cost of moving money.",
    examples: "Business loan interest, business credit-card interest, monthly account fees, Stripe/Square processing fees.",
    rule: "Interest is deductible only when the debt is for the business. Keep business and personal accounts separate.",
  },
  {
    name: "Business Insurance", Icon: ShieldCheck,
    desc: "Policies that protect your business, property, and people.",
    examples: "General liability, professional/E&O, commercial property, workers' comp, business interruption.",
    rule: "Deductible. Self-employed health insurance is claimed separately, not here.",
  },
  {
    name: "Contract Labor and Commissions", Icon: Users,
    desc: "Payments to people who aren't your employees.",
    examples: "Freelancers, subcontractors, 1099 contractors, referral or sales commissions.",
    rule: "Collect a W-9 up front and file Form 1099-NEC for anyone paid $600+ in the year.",
  },
  {
    name: "Employee Training and Development", Icon: GraduationCap,
    desc: "Sharpening the skills you or your staff already use.",
    examples: "Courses, certifications, workshops, industry conferences, professional books.",
    rule: "Must maintain or improve skills for your current business — not qualify you for a new career.",
  },
  {
    name: "Employee Wages and Benefits", Icon: Users,
    desc: "Compensation paid to actual employees on payroll.",
    examples: "Salaries, hourly wages, bonuses, employer payroll taxes, retirement contributions.",
    rule: "Owner draws are NOT wages in a sole prop or partnership. Payroll must be run properly.",
  },
  {
    name: "Home Office Expenses", Icon: Home,
    desc: "The share of your home used regularly and only for business.",
    examples: "Simplified: $5/sq ft. Actual: business % of rent, utilities, insurance, repairs.",
    rule: "Space must be exclusive & regular. Simplified method caps at 300 sq ft ($1,500).",
  },
  {
    name: "Licenses Permits and Dues", Icon: BadgeCheck,
    desc: "What it costs to operate legally and stay in your profession.",
    examples: "Business license, trade permits, professional association dues, LLC filing fees.",
    rule: "The lobbying/political portion of any dues is not deductible.",
  },
  {
    name: "Office Supplies and Office Expenses", Icon: Package,
    desc: "Everyday consumables and small office running costs.",
    examples: "Paper, pens, postage, printer ink, cleaning supplies, small tools.",
    rule: "Bigger equipment isn't a supply — it may be depreciated or expensed under Section 179.",
  },
  {
    name: "Professional Services", Icon: Building2,
    desc: "Fees paid to experts who help run your business.",
    examples: "Accountant/CPA, bookkeeper, attorney, consultants, business tax prep.",
    rule: "Personal legal or tax-prep fees are not deductible — only the business portion.",
  },
  {
    name: "Rent or Lease", Icon: Building2,
    desc: "Rent for space or equipment you use for the business.",
    examples: "Office/shop rent, equipment leases, business vehicle lease (business %).",
    rule: "Leased vehicles have luxury-auto inclusion limits. Don't stack this with a home-office claim.",
  },
  {
    name: "Repairs Maintenance and Equipment", Icon: Wrench,
    desc: "Keeping property and equipment in working order.",
    examples: "Equipment repairs, routine tool maintenance, minor fixes.",
    rule: "Repairs are expensed now; improvements that add value are capitalized and depreciated.",
  },
  {
    name: "Research and Development", Icon: FlaskConical,
    desc: "Creating new or improved products, processes, or software.",
    examples: "Prototyping, testing, development labor and materials.",
    rule: "R&D costs are amortized under Section 174 — talk to your CPA; you may qualify for the R&D credit.",
  },
  {
    name: "Taxes and Government Fees", Icon: Landmark,
    desc: "Business taxes and charges paid to a government.",
    examples: "State/local business taxes, employer payroll taxes, sales tax remitted, business property tax.",
    rule: "Federal income tax is not deductible here. Self-employment tax has its own separate adjustment.",
  },
  {
    name: "Technology and Software", Icon: Cpu,
    desc: "The digital tools and subscriptions that run your business.",
    examples: "SaaS subscriptions, cloud storage, accounting apps, hosting, computers.",
    rule: "Subscriptions are expensed. Hardware may qualify for Section 179 expensing.",
  },
  {
    name: "Travel Meals and Entertainment", Icon: Plane,
    desc: "Business trips and meals with a clear business purpose.",
    examples: "Airfare, lodging, 50% of business meals, rental cars, business mileage.",
    rule: "Meals 50% deductible; entertainment 0%. Log who, what, and why. Travel must be primarily for business.",
  },
  {
    name: "Utilities", Icon: Zap,
    desc: "Services that keep your business location running.",
    examples: "Electricity, water, gas, internet, dedicated business phone line.",
    rule: "Home-based? Deduct only the business-use portion (or use the home-office simplified method).",
  },
  {
    name: "Vehicle Expenses", Icon: Car,
    desc: "The cost of driving for your business.",
    examples: "Standard mileage (72.5¢/76¢ for 2026) OR actual costs: gas, insurance, repairs, depreciation.",
    rule: "Pick one method per vehicle per year — you can't mix. Log business miles; commuting doesn't count.",
  },
];

const RECORDS = [
  { what: "Receipts & invoices", how: "Every business purchase and sale — keep the itemized receipt, not just the card charge." },
  { what: "Bank & card statements", how: "Business accounts only. Reconcile monthly so nothing is missed." },
  { what: "Mileage log", how: "Date, miles, and business purpose for each trip — kept as you drive, not reconstructed later." },
  { what: "Meal & travel notes", how: "Who was there, the business purpose, date, and amount." },
  { what: "1099s & W-9s", how: "Collect a W-9 before paying a contractor; keep copies of every 1099 you issue or receive." },
  { what: "Asset & depreciation records", how: "Purchase date, cost, and in-service date for equipment and vehicles." },
];

const RETENTION = [
  { years: "3 years", note: "General rule — from the date you filed the return." },
  { years: "6 years", note: "If you under-reported income by more than 25%." },
  { years: "7 years", note: "For a bad-debt deduction or worthless-securities claim." },
  { years: "4 years", note: "Employment/payroll tax records." },
  { years: "Life of asset + 3 yrs", note: "Property & depreciation records." },
  { years: "Keep forever", note: "Copies of the filed tax returns themselves." },
];

const MISTAKES = [
  "Mixing personal and business money in one account",
  "No contemporaneous mileage log (reconstructing it later)",
  "Deducting 100% of business meals — the limit is 50%",
  "Writing off entertainment — it's 0% deductible",
  "Skipping 1099-NEC filings for contractors paid $600+",
  "Claiming a home office that isn't used exclusively for work",
  "Deducting your commute — commuting miles never count",
  "Expensing large assets that should be depreciated (or vice-versa)",
  "Tossing receipts for cash purchases",
  "Deducting an owner 'salary' in a sole proprietorship",
  "Forgetting quarterly estimated tax payments",
];

function Section({ id, title, sub, children }) {
  return (
    <section data-testid={`tax-section-${id}`} className="mb-12">
      <div className="mb-5">
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[var(--text)]">{title}</h2>
        {sub && <p className="text-sm text-[var(--text-2)] mt-1">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

const MILEAGE_RATES_2026 = [
  { period: "Jan – Jun 2026", rate: 0.725, label: "72.5¢ / mile" },
  { period: "Jul – Dec 2026", rate: 0.76, label: "76¢ / mile" },
];
const currentMileageRate = () => (new Date().getMonth() <= 5 ? 0.725 : 0.76);

function MileageRules() {
  const [miles, setMiles] = useState("");
  const rate = currentMileageRate();
  const deduction = (parseFloat(miles) || 0) * rate;
  return (
    <Section id="mileage" title="IRS Mileage Rules" sub="Which drives count — and what they're worth.">
      <div className="border border-[var(--line)] bg-white p-6" data-testid="tax-mileage-card">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 bg-[var(--accent)]/10 flex items-center justify-center"><Car className="w-5 h-5 text-[var(--accent)]" /></div>
          <h3 className="font-serif text-xl sm:text-2xl tracking-tight">What Drives Are Tax Deductible?</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="label-mono text-[var(--text-2)] mb-2">2026 Mileage Rates</p>
            <div className="space-y-2">
              {MILEAGE_RATES_2026.map((r) => (
                <div key={r.period} data-testid="tax-mileage-rate" className="flex items-center justify-between border border-[var(--line)] bg-[#f9f9f7] px-3 py-2.5">
                  <span className="text-sm text-[var(--text-2)]">{r.period}</span>
                  <span className="font-mono font-bold text-[var(--text)]">{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="label-mono text-[var(--danger)] flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> Never deductible</p>
              <p className="text-sm text-[var(--text)]">Commuting from home to your regular workplace.</p>
            </div>
            <div>
              <p className="label-mono text-emerald-600 flex items-center gap-1.5 mb-2"><CheckSquare className="w-4 h-4" /> Always deductible</p>
              <ul className="text-sm text-[var(--text)] space-y-1">
                {["Job site to job site", "Client visits", "Business errands", "Driving between deliveries", "Home to a temporary job site"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">•</span> {x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-[var(--line)]">
          <p className="label-mono text-[var(--accent)] flex items-center gap-1.5 mb-2"><Home className="w-4 h-4" /> Home office exception</p>
          <p className="text-sm text-[var(--text)]">If you qualify for the home office deduction, your home counts as your principal place of business — so your <b>first and last drives of the day become deductible business miles</b> instead of a non-deductible commute.</p>
          <p className="text-sm text-[var(--text-2)] mt-2"><b>The plain-English test:</b> you must use a specific area of your home <b>regularly and exclusively</b> for your business, and it has to be your <b>principal place of business</b> (where you handle your admin/management work with no other fixed location for it). Pass that, and the trips from your home office out to your first job and back from your last job count as business miles.</p>
        </div>

        <div className="mt-6 pt-5 border-t border-[var(--line)]" data-testid="tax-mileage-estimator">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-1.5 mb-3"><Calculator className="w-4 h-4 text-[var(--accent)]" /> Mileage savings estimator</p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <label className="flex-1">
              <span className="text-xs text-[var(--text-2)]">Estimated annual business miles</span>
              <input data-testid="tax-mileage-miles" type="number" min="0" value={miles} onChange={(e) => setMiles(e.target.value)} placeholder="e.g. 8,000" className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)] font-mono" />
            </label>
            <div className="sm:text-right">
              <p className="text-xs text-[var(--text-2)]">Estimated deduction <span className="font-mono">@ {(rate * 100).toFixed(1)}¢/mi</span></p>
              <p data-testid="tax-mileage-deduction" className="font-mono text-3xl font-bold text-[var(--accent)]">{money2(deduction)}</p>
            </div>
          </div>
          <p className="text-xs text-[var(--text-2)] mt-3">Uses the current 2026 rate ({(rate * 100).toFixed(1)}¢/mi for {rate === 0.725 ? "Jan–Jun" : "Jul–Dec"}). A quick estimate — your actual deduction depends on your mileage log and return. Confirm with your CPA.</p>
        </div>
      </div>
    </Section>
  );
}

function DeductionEstimator() {
  const [miles, setMiles] = useState("");
  const [rate, setRate] = useState(0.725);
  const [sqft, setSqft] = useState("");
  const mileDed = (parseFloat(miles) || 0) * rate;
  const capped = Math.min(parseFloat(sqft) || 0, 300);
  const officeDed = capped * 5;
  const overCap = (parseFloat(sqft) || 0) > 300;
  const [sending, setSending] = useState(false);
  const sendMiles = async () => {
    const m = parseFloat(miles) || 0;
    if (m <= 0) return toast.error("Enter business miles first");
    setSending(true);
    try {
      await api.post("/cash-miles", { miles: m, date: new Date().toISOString().slice(0, 10), start_location: "Tax estimator" });
      toast.success(`${m} mi sent to Cash Miles`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send to Cash Miles");
    } finally {
      setSending(false);
    }
  };
  return (
    <Section id="estimator" title="Deduction Estimator" sub="Fast, back-of-napkin estimates — not a filing, just clarity.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-est-mileage">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Car className="w-4 h-4 text-[var(--accent)]" /> Mileage</p>
          <span className="label-mono text-[var(--text-2)]">Business miles</span>
          <input data-testid="tax-est-miles" type="number" value={miles} onChange={(e) => setMiles(e.target.value)} placeholder="0" className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <span className="label-mono text-[var(--text-2)] mt-3 block">Rate</span>
          <select data-testid="tax-est-rate" value={rate} onChange={(e) => setRate(parseFloat(e.target.value))} className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
            <option value={0.725}>72.5¢ — Jan–Jun 2026</option>
            <option value={0.76}>76¢ — Jul–Dec 2026</option>
          </select>
          <p className="mt-3 text-sm text-[var(--text-2)]">Estimated deduction</p>
          <p data-testid="tax-est-mileage-result" className="font-mono text-2xl font-bold text-[var(--accent)]">{money2(mileDed)}</p>
          <button data-testid="tax-est-send-miles" onClick={sendMiles} disabled={sending} className="mt-3 inline-flex items-center gap-2 h-9 px-3 border border-[var(--accent)] text-[var(--accent)] text-sm hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-50">
            <Car className="w-4 h-4" /> {sending ? "Sending…" : "Send miles to Cash Miles"}
          </button>
        </div>
        <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-est-office">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Home className="w-4 h-4 text-[var(--accent)]" /> Home office (simplified)</p>
          <span className="label-mono text-[var(--text-2)]">Office square feet</span>
          <input data-testid="tax-est-sqft" type="number" value={sqft} onChange={(e) => setSqft(e.target.value)} placeholder="0" className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <p className="mt-2 text-xs text-[var(--text-2)]">$5 / sq ft · capped at 300 sq ft ({money2(1500)} max).{overCap ? " Capped at 300 sq ft." : ""}</p>
          <p className="mt-3 text-sm text-[var(--text-2)]">Estimated deduction</p>
          <p data-testid="tax-est-office-result" className="font-mono text-2xl font-bold text-[var(--accent)]">{money2(officeDed)}</p>
        </div>
        <div className="border border-[var(--accent)]/30 bg-[var(--accent)]/5 p-5 flex flex-col justify-center" data-testid="tax-est-total">
          <p className="label-mono text-[var(--accent)] flex items-center gap-2 mb-2"><Calculator className="w-4 h-4" /> Combined estimate</p>
          <p data-testid="tax-est-total-result" className="font-mono text-4xl font-bold text-[var(--text)]">{money2(mileDed + officeDed)}</p>
          <p className="text-xs text-[var(--text-2)] mt-2">A rough estimate of these two deductions for the year. Your actual number depends on your full return — confirm with your CPA.</p>
        </div>
      </div>
    </Section>
  );
}

function LogExpenseModal({ cat, onClose }) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return toast.error("Enter a vendor or description");
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) return toast.error("Enter an amount");
    setBusy(true);
    try {
      await api.post("/snapbudget/log-bill", {
        title, amount: amt, category: "business", biz_category: cat.name,
        date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Logged to ${cat.name} in Business SnapBudget`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log — SnapBudget access required");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={!!cat} onClose={onClose} testid="tax-log-expense-modal" subtitle={cat?.name} title="Log a business expense">
      <div className="space-y-4">
        <p className="text-sm text-[var(--text-2)]">Posts to <span className="font-medium text-[var(--text)]">{cat?.name}</span> in your Business SnapBudget for this month.</p>
        <div>
          <span className="label-mono text-[var(--text-2)]">Vendor / description</span>
          <input data-testid="tax-log-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Adobe subscription" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>
        <div>
          <span className="label-mono text-[var(--text-2)]">Amount ($)</span>
          <input data-testid="tax-log-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>
        <button data-testid="tax-log-submit" onClick={submit} disabled={busy} className="w-full h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
          {busy ? "Logging…" : "Log to SnapBudget"}
        </button>
      </div>
    </Modal>
  );
}

function QuarterlyReminders({ navigate }) {
  const year = new Date().getFullYear();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [setAside, setSetAside] = useState(null);
  useEffect(() => {
    api.get(`/snapbudget/schedule-c?year=${year}`).then((r) => {
      const net = r.data.net || 0;
      setSetAside({ net, quarter: Math.max(0, net) * 0.25 / 4 });
    }).catch(() => {});
  }, [year]);
  const dates = [
    { q: "Q1", label: `Apr 15, ${year}` },
    { q: "Q2", label: `Jun 15, ${year}` },
    { q: "Q3", label: `Sep 15, ${year}` },
    { q: "Q4", label: `Jan 15, ${year + 1}` },
  ];
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tax/quarterly-reminders", { year });
      setDone(true);
      toast.success(data.created > 0 ? `Added ${data.created} estimated-tax reminder${data.created === 1 ? "" : "s"}` : "You already have these reminders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add reminders");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section id="quarterly" title="Estimated Tax Reminders" sub="Never miss a quarterly payment — added straight to your reminders.">
      <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-quarterly">
        {setAside && setAside.quarter > 0 && (
          <div data-testid="tax-setaside" className="mb-4 flex items-start gap-3 border border-[var(--accent)]/30 bg-[var(--accent)]/5 px-4 py-3">
            <PiggyBank className="w-5 h-5 text-[var(--accent)] shrink-0 mt-0.5" />
            <p className="text-sm text-[var(--text)]">Suggested set-aside: <span className="font-mono font-bold" data-testid="tax-setaside-amount">{money2(setAside.quarter)}</span> per quarter <span className="text-[var(--text-2)]">(25% of your tracked net {money2(setAside.net)}/yr)</span></p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="tax-quarterly-dates">
          {dates.map((d) => (
            <div key={d.q} className="border border-[var(--line)] p-3 text-center">
              <p className="label-mono text-[var(--accent)]">{d.q} {year}</p>
              <p className="text-sm font-medium mt-1">{d.label}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="tax-quarterly-add" onClick={add} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 bg-[var(--accent)] text-white text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            <Bell className="w-4 h-4" /> {busy ? "Adding…" : `Add ${year} quarterly reminders`}
          </button>
          {done && (
            <button data-testid="tax-quarterly-view" onClick={() => navigate("/dashboard?mode=business&tab=reminders")} className="text-sm text-[var(--accent)] hover:underline">View in reminders →</button>
          )}
        </div>
        <p className="text-xs text-[var(--text-2)] mt-3">Set aside roughly 25–30% of net business income for taxes. Dates shift to the next business day if they land on a weekend or holiday.</p>
      </div>
    </Section>
  );
}

function ScheduleCSummary() {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [data, setData] = useState(null);
  const [sqft, setSqft] = useState(() => parseInt(localStorage.getItem("tax_home_office_sqft") || "0", 10) || 0);
  useEffect(() => {
    api.get(`/snapbudget/schedule-c?year=${year}&home_office_sqft=${sqft || 0}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [year, sqft]);
  const setSqftPersist = (v) => {
    const n = Math.max(0, Math.min(300, parseInt(v || "0", 10) || 0));
    setSqft(n);
    localStorage.setItem("tax_home_office_sqft", String(n));
  };
  const baseRows = data ? [...data.categories, ...(data.other || [])].filter((r) => r.amount > 0) : [];
  const addons = [];
  if (data?.mileage?.deduction > 0) addons.push({ name: `Vehicle Expenses (mileage · ${data.mileage.miles} mi @ $${data.mileage.rate}/mi)`, amount: data.mileage.deduction });
  if (data?.home_office?.deduction > 0) addons.push({ name: `Home Office (simplified · ${data.home_office.sqft} sq ft)`, amount: data.home_office.deduction });
  const rows = [...baseRows, ...addons];
  const exportCsv = () => {
    const lines = [["Schedule C Category", "Amount"]];
    rows.forEach((r) => lines.push([r.name, r.amount.toFixed(2)]));
    lines.push(["Total expenses", (data.total_expenses || 0).toFixed(2)]);
    lines.push(["Business income (tracked)", (data.total_income || 0).toFixed(2)]);
    lines.push(["Net", (data.net || 0).toFixed(2)]);
    const csv = lines.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `schedule-c-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    try {
      const { data: blob } = await api.get(`/snapbudget/schedule-c.pdf?year=${year}&home_office_sqft=${sqft || 0}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Could not build PDF");
    }
  };
  const years = [nowY, nowY - 1, nowY - 2];
  return (
    <Section id="schedule-c" title="Year-End Schedule C Summary" sub="Every business category — plus mileage & home office — totalled for the year.">
      <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-schedule-c">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="tax-sc-year" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-10 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-[var(--text-2)]">
              Home office sq ft
              <input data-testid="tax-sc-sqft" type="number" value={sqft || ""} onChange={(e) => setSqftPersist(e.target.value)} placeholder="0" className="w-20 h-10 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="tax-sc-export" onClick={exportCsv} disabled={!rows.length} className="inline-flex items-center gap-2 h-10 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <FileText className="w-4 h-4" /> CSV
            </button>
            <button data-testid="tax-sc-pdf" onClick={exportPdf} disabled={!rows.length} className="inline-flex items-center gap-2 h-10 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Download className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-[var(--text-2)] py-6 text-center" data-testid="tax-sc-empty">No business figures for {year} yet. Tag expenses as Business in SnapBudget (and log miles) and they'll total here.</p>
        ) : (
          <table className="w-full text-sm" data-testid="tax-sc-table">
            <thead><tr className="text-left label-mono text-[var(--text-2)] border-b border-[var(--line)]"><th className="py-2">Category</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} data-testid="tax-sc-row" className="border-b border-[var(--line)]/60">
                  <td className="py-2">{r.name}</td>
                  <td className="py-2 text-right font-mono">{money2(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold"><td className="py-2">Total expenses</td><td className="py-2 text-right font-mono text-[var(--danger)]">{money2(data.total_expenses)}</td></tr>
              <tr><td className="py-1 text-[var(--text-2)]">Business income (tracked)</td><td className="py-1 text-right font-mono text-emerald-600">{money2(data.total_income)}</td></tr>
              <tr className="font-bold"><td className="py-1">Net</td><td className="py-1 text-right font-mono">{money2(data.net)}</td></tr>
            </tfoot>
          </table>
        )}
        <p className="text-xs text-[var(--text-2)] mt-4">Includes business-tagged SnapBudget expenses, your logged mileage, and the simplified home-office estimate for {year}. Confirm against your books before filing.</p>
      </div>
    </Section>
  );
}

function Locked({ onUpgrade, navigate }) {
  return (
    <div className="min-h-screen app-canvas" data-testid="tax-hub-locked">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="tax-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--accent)] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Briefing
        </button>
        <div className="border border-[var(--line)] bg-white p-8 sm:p-12 text-center">
          <img src="/tax-clarity-hub.png" alt="Tax Clarity Hub" className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-5" />
          <p className="label-mono text-[var(--accent)] flex items-center justify-center gap-2"><Crown className="w-4 h-4" /> Business Platinum</p>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3">Tax Clarity Hub</h1>
          <p className="text-lg text-[var(--text)] mt-4 max-w-xl mx-auto font-serif italic">
            "Your app tells you what's deductible — as you spend."
          </p>
          <p className="text-sm text-[var(--text-2)] mt-4 max-w-xl mx-auto">
            A plain-English IRS Schedule C reference built right into your budget: every business
            category explained, the 2026 key numbers, record-keeping rules, and the mistakes that
            trigger audits. Included with Business Platinum.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
            <button data-testid="tax-upgrade" onClick={onUpgrade} className="inline-flex items-center gap-2 h-11 px-6 bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 transition-opacity">
              <Crown className="w-4 h-4" /> Unlock with Platinum
            </button>
            <button data-testid="tax-see-pricing" onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 h-11 px-6 border border-[var(--line)] bg-white text-sm hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
              See pricing
            </button>
          </div>
          <p className="text-[11px] text-[var(--text-2)] mt-6">General guidance, not tax advice. Confirm specifics with your CPA.</p>
        </div>
      </div>
    </div>
  );
}

export default function TaxClarityHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openUpgrade } = usePremium();
  const [logCat, setLogCat] = useState(null);
  const hasAccess = !!(user && (user.platinum_active || user.role === "admin"));

  if (!hasAccess) return <Locked onUpgrade={openUpgrade} navigate={navigate} />;

  return (
    <div className="min-h-screen app-canvas" data-testid="tax-clarity-hub">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="tax-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--accent)] transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Briefing
        </button>

        {/* Hero */}
        <div data-testid="tax-hero" className="relative overflow-hidden rounded-xl border border-emerald-500/20 px-6 sm:px-10 py-10 mb-12" style={{ backgroundImage: "linear-gradient(90deg, rgba(5,9,12,0.94) 0%, rgba(5,9,12,0.78) 46%, rgba(5,9,12,0.35) 100%), url('/tax-hub-hero.jpg')", backgroundSize: "cover", backgroundPosition: "center" }}>
          <div className="relative flex items-start gap-5">
            <img src="/tax-clarity-hub.png" alt="Tax Clarity Hub" className="hidden sm:block w-20 h-20 rounded-2xl shadow-lg shrink-0 ring-1 ring-white/10" />
            <div className="min-w-0">
          <p className="label-mono text-emerald-300 flex items-center gap-2"><Landmark className="w-4 h-4" /> Tax Clarity Hub · Business Platinum</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 text-white">Know what's deductible — <span className="text-emerald-300">as you spend.</span></h1>
          <p className="text-base text-slate-200/90 mt-4 max-w-2xl">
            A clean reference for U.S. sole proprietors and small businesses. Every IRS Schedule C
            category in plain English, the 2026 numbers that matter, and how to keep records that hold up.
          </p>
          <p className="text-[11px] text-slate-300/70 mt-4">General guidance for the 2026 tax year — not tax advice. Confirm your situation with a CPA.</p>
            </div>
          </div>
        </div>

        {/* Key numbers */}
        <Section id="key-numbers" title="2026 Key Numbers" sub="The figures you'll reach for most this year.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tax-key-numbers">
            {KEY_NUMBERS.map((k) => (
              <div key={k.label} data-testid="tax-key-number" className="border border-[var(--line)] bg-white p-5">
                <div className="flex items-center gap-2 text-[var(--text-2)]"><k.Icon className="w-4 h-4 text-[var(--accent)]" /> <span className="label-mono">{k.label}</span></div>
                <p className="font-mono text-2xl font-bold mt-2 text-[var(--text)]">{k.value}</p>
                <p className="text-xs text-[var(--text-2)] mt-1">{k.sub}</p>
              </div>
            ))}
          </div>
        </Section>

        <MileageRules />

        <DeductionEstimator />

        <QuarterlyReminders navigate={navigate} />

        {/* Categories */}
        <Section id="categories" title="Schedule C Categories" sub="Tag any expense to a category in SnapBudget — here's what each one means.">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tax-categories">
            {CATEGORIES.map((c) => (
              <article key={c.name} data-testid="tax-category-card" className="border border-[var(--line)] bg-white p-5 flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 bg-[var(--accent)]/10 flex items-center justify-center"><c.Icon className="w-4.5 h-4.5 text-[var(--accent)]" /></div>
                  <h3 className="font-serif text-lg leading-tight tracking-tight mt-0.5">{c.name}</h3>
                </div>
                <p className="text-sm text-[var(--text)] mt-3">{c.desc}</p>
                <div className="mt-3">
                  <p className="label-mono text-[var(--text-2)]">Examples</p>
                  <p className="text-sm text-[var(--text-2)] mt-1">{c.examples}</p>
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--line)]">
                  <p className="label-mono text-[var(--accent)]">Key rule</p>
                  <p className="text-sm text-[var(--text)] mt-1">{c.rule}</p>
                </div>
                <button data-testid="tax-log-expense" onClick={() => setLogCat(c)} className="mt-4 inline-flex items-center justify-center gap-2 h-9 px-3 border border-[var(--accent)] text-[var(--accent)] text-sm hover:bg-[var(--accent)] hover:text-white transition-colors">
                  <Plus className="w-4 h-4" /> Log an expense here
                </button>
              </article>
            ))}
          </div>
        </Section>

        <ScheduleCSummary />

        {/* Record keeping */}
        <Section id="records" title="Record Keeping" sub="What to document, and how long to keep it.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-records-what">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-[var(--accent)]" /> What to document</p>
              <ul className="space-y-3">
                {RECORDS.map((r) => (
                  <li key={r.what} className="text-sm">
                    <span className="font-medium text-[var(--text)]">{r.what}</span>
                    <span className="text-[var(--text-2)]"> — {r.how}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-records-how-long">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-[var(--accent)]" /> How long to keep it</p>
              <ul className="space-y-3">
                {RETENTION.map((r) => (
                  <li key={r.years} className="flex items-start gap-3 text-sm">
                    <span className="font-mono font-bold text-[var(--accent)] shrink-0 w-32">{r.years}</span>
                    <span className="text-[var(--text-2)]">{r.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div data-testid="tax-receipt-policy" className="mt-4 border border-[var(--accent)]/30 bg-[var(--accent)]/5 p-5 rounded-lg">
            <p className="font-medium text-[var(--text)] flex items-center gap-2"><Receipt className="w-4 h-4 text-[var(--accent)]" /> Require receipts for card transactions over $75</p>
            <p className="text-sm text-[var(--text-2)] mt-1.5">The IRS may require receipts for business expenses of $75 or more. Manage this and other spend policies from your <span data-testid="tax-policies-link" role="link" onClick={() => navigate("/dashboard?mode=business&tab=policies")} className="cursor-pointer font-semibold text-[var(--accent)] underline underline-offset-2">Policies page</span>.</p>
          </div>
        </Section>
        <Section id="mistakes" title="Common Mistakes" sub="A quick self-check before you file.">
          <div className="border border-[var(--line)] bg-white p-5" data-testid="tax-mistakes">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {MISTAKES.map((m) => (
                <li key={m} data-testid="tax-mistake-item" className="flex items-start gap-3 text-sm">
                  <CheckSquare className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent)]" />
                  <span className="text-[var(--text)]">{m}</span>
                </li>
              ))}
            </ul>
            <p className="flex items-start gap-2 text-xs text-[var(--text-2)] mt-5 pt-4 border-t border-[var(--line)]">
              <AlertTriangle className="w-4 h-4 shrink-0 text-[var(--accent)]" />
              This hub is general educational guidance for the 2026 tax year, not tax advice. Rules change and situations vary — always confirm with a qualified CPA or tax professional.
            </p>
          </div>
        </Section>
      </div>

      <LogExpenseModal key={logCat?.name || "none"} cat={logCat} onClose={() => setLogCat(null)} />
    </div>
  );
}
